import { setup } from 'xstate';
import {
  type ProposalSnapshot,
  type ProposalTarget,
  decisionBlockReason,
} from './edit-proposal.js';

/**
 * F-016 `editProposalMachine` (§XI — data-model §2.1). The server is the
 * transition authority: the apply/reject use cases restore a snapshot from
 * the persisted status, send APPLY/REJECT, and persist the resulting state.
 * Guard predicates are the SAME `decisionBlockReason` the derived-status
 * projection uses (I-CC-03/04) — the machine and the read model share one
 * truth. Stale/expired are NOT states here: they are guard-blocked
 * projections of `pending`; the caller surfaces the block reason.
 */
export type EditProposalContext = {
  readonly proposal: ProposalSnapshot;
  readonly target: ProposalTarget;
  readonly nowMs: number;
};

export type EditProposalEvent = { readonly type: 'APPLY' } | { readonly type: 'REJECT' };

export const editProposalMachine = setup({
  types: {} as {
    context: EditProposalContext;
    input: EditProposalContext;
    events: EditProposalEvent;
  },
  guards: {
    applyAllowed: ({ context }) =>
      decisionBlockReason(context.proposal, context.target, context.nowMs) === undefined,
    // Rejection is refused ONLY when already decided (data-model §2.1:
    // REJECT [!decidedAlready]) — a designer may reject a stale or even an
    // expired proposal to clear it; only APPLY is expiry/drift-blocked.
    rejectAllowed: ({ context }) => context.proposal.status === 'pending',
  },
}).createMachine({
  id: 'editProposal',
  initial: 'pending',
  context: ({ input }) => input,
  states: {
    pending: {
      on: {
        APPLY: { target: 'applied', guard: 'applyAllowed' },
        REJECT: { target: 'rejected', guard: 'rejectAllowed' },
      },
    },
    applied: { type: 'final' },
    rejected: { type: 'final' },
  },
});
